import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '@/App';
import { toast } from 'sonner';
import { ShoppingCart } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

export const ProductCard = ({ product }) => {
  const { addToCart, loading } = useCart();

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await addToCart(product.product_id);
      toast.success(`${product.name} added to cart!`);
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  const discount = product.original_price 
    ? Math.round((1 - product.price / product.original_price) * 100)
    : null;

  const sportColors = {
    football: 'bg-green-600',
    basketball: 'bg-orange-500',
    baseball: 'bg-blue-600',
    soccer: 'bg-purple-600',
    multi: 'bg-[#C41E3A]'
  };

  return (
    <Link 
      to={`/products/${product.product_id}`} 
      className="card-product group block"
      data-testid={`product-card-${product.product_id}`}
    >
      {/* Image Container */}
      <div className="relative aspect-square overflow-hidden bg-[#111111]">
        <img
          src={product.images?.[0] || 'https://images.pexels.com/photos/7783409/pexels-photo-7783409.jpeg'}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {product.featured && (
            <Badge className="bg-[#C41E3A] text-white font-barlow uppercase text-xs px-2 py-1">
              Featured
            </Badge>
          )}
          {discount && (
            <Badge className="bg-white text-black font-barlow uppercase text-xs px-2 py-1">
              {discount}% Off
            </Badge>
          )}
        </div>

        {/* Sport Badge */}
        <div className="absolute top-3 right-3">
          <Badge className={`${sportColors[product.sport] || 'bg-[#27272A]'} text-white font-barlow uppercase text-xs px-2 py-1`}>
            {product.sport}
          </Badge>
        </div>

        {/* Quick Add Button */}
        <button
          onClick={handleAddToCart}
          disabled={loading || !product.in_stock}
          className="absolute bottom-3 right-3 p-3 bg-[#C41E3A] text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-[#A01830] disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid={`add-to-cart-${product.product_id}`}
        >
          <ShoppingCart className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Category & Authentication */}
        <div className="flex items-center gap-2 mb-2">
          <span className="label-caps text-white/50">{product.category?.replace('_', ' ')}</span>
          {product.authentication && (
            <>
              <span className="text-white/30">•</span>
              <span className="label-caps text-[#007AFF]">{product.authentication}</span>
            </>
          )}
        </div>

        {/* Title */}
        <h3 className="font-barlow font-bold text-lg text-white mb-2 line-clamp-2 group-hover:text-[#C41E3A] transition-colors duration-300">
          {product.name}
        </h3>

        {/* Athlete & Team */}
        {(product.athlete || product.team) && (
          <p className="text-white/50 text-sm mb-3">
            {product.athlete}{product.athlete && product.team && ' • '}{product.team}
          </p>
        )}

        {/* Price */}
        <div className="flex items-center gap-3">
          <span className="font-barlow font-black text-xl text-white">
            ${product.price.toFixed(2)}
          </span>
          {product.original_price && (
            <span className="text-white/40 line-through">
              ${product.original_price.toFixed(2)}
            </span>
          )}
        </div>

        {/* Stock Status */}
        {!product.in_stock && (
          <span className="inline-block mt-2 text-[#C41E3A] text-sm font-bold uppercase">
            Sold Out
          </span>
        )}
      </div>
    </Link>
  );
};

export default ProductCard;
